import { type HTMLAttributes } from 'react';
import { cn } from '../lib/cn';

export type Status = 'active' | 'expiring' | 'lapsed' | 'never' | 'neutral';

const STATUS: Record<Status, { label: string; cls: string; dot: string }> = {
  active: { label: 'Active', cls: 'bg-ok-soft text-ok-ink', dot: 'bg-ok' },
  expiring: { label: 'Expiring', cls: 'bg-warn-soft text-warn-ink', dot: 'bg-warn' },
  lapsed: { label: 'Lapsed', cls: 'bg-bad-soft text-bad-ink', dot: 'bg-bad' },
  never: { label: 'Never paid', cls: 'bg-neutral-soft text-neutral-ink', dot: 'bg-neutral' },
  neutral: { label: 'Inactive', cls: 'bg-neutral-soft text-neutral-ink', dot: 'bg-neutral' },
};

export interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  status: Status;
  /** Override the default label text (e.g. "Expiring in 3d"). */
  children?: React.ReactNode;
}

/** Member lifecycle badge. Carries a dot so status survives grayscale / color-blindness. */
export function StatusBadge({ status, children, className, ...props }: StatusBadgeProps) {
  const s = STATUS[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-medium',
        s.cls,
        className,
      )}
      {...props}
    >
      <span className={cn('size-1.5 rounded-full', s.dot)} aria-hidden />
      {children ?? s.label}
    </span>
  );
}

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: 'accent' | 'dark' | 'muted';
}

export function Badge({ className, tone = 'muted', ...props }: BadgeProps) {
  const tones = {
    accent: 'bg-accent text-accent-ink',
    dark: 'bg-dark text-on-dark',
    muted: 'bg-surface text-ink-soft',
  };
  return (
    <span
      className={cn('inline-flex items-center rounded-pill px-2.5 py-1 text-xs font-medium', tones[tone], className)}
      {...props}
    />
  );
}
